<template>
    <applicants-find
        v-slot="{
            data: applicant,
            promise: applicantsFind
        }"
        :params="{
            applicant: $route.params.applicant
        }"
        :include="[
            'address',
            'application(include:listing(include:property(include:address)))',
            'employers',
            'landlord',
        ]"
        :find-on-created="false"
    >
        <applications-applicants-get
            v-slot="{
                data: coapplicants,
                promise: applicationsApplicantsGet
            }"
            :params="{
                application: $route.params.application,
                is_co_applicant: true
            }"
            :get-on-created="false"
        >
            <promise-all
                v-slot="{
                    has_loaded,
                    is_loading,
                    errors: find_errors,
                    execute: find
                }"
                :execute-on-created="true"
                :callstack="{
                    applicant: () => applicantsFind(),
                    coapplicants: () => applicationsApplicantsGet()
                }"
            >
                <div>
                    <template v-if="!has_loaded">
                        <base-loading
                            :error="_.first(find_errors)"
                            :retry="find"
                            class="py-6"
                        />

                        <div
                            v-for="(find_error, index) in find_errors"
                            :key="index"
                        >
                            <error-modal
                                v-if="find_error && $error.is(find_error, { exclude: [400, 422] })"
                                :error="find_error"
                            />
                        </div>
                    </template>
                    <template v-else>
                        <applicants-submission-store
                            v-slot="{
                                is_loading: is_saving,
                                error: store_error,
                                store
                            }"
                            :params="{
                                applicant: $route.params.applicant
                            }"
                            @success="onSuccess"
                        >
                            <base-form @submit="store(form)">
                                <div class="px-4 py-6 border-b sm:px-6">
                                    <form-section>
                                        <template slot="title">
                                            <span class="text-grey-darkest">Listing</span>
                                        </template>
                                        <template slot="description">
                                            This is the listing that you're applying for.
                                        </template>

                                        <grid-layout>
                                            <grid-layout-item class="mb-3 sm:w-1/2 sm:mb-0">
                                                <div class="text-xs tracking-wide uppercase text-grey-darker">Property</div>
                                                <div class="text-sm text-grey-darkest">
                                                    <base-address :address="applicant.application.listing.property.address" />
                                                </div>
                                            </grid-layout-item>

                                            <grid-layout-item class="sm:w-1/2">
                                                <div class="text-xs tracking-wide uppercase text-grey-darker">Rent</div>
                                                <div class="mb-3 text-sm text-grey-darkest">
                                                    {{ applicant.application.listing.rent_amount | money(0) }}
                                                </div>

                                                <div class="text-xs tracking-wide uppercase text-grey-darker">Deposit</div>
                                                <div class="mb-3 text-sm text-grey-darkest">
                                                    {{ applicant.application.listing.deposit_amount | money(0) }}
                                                </div>
                                            </grid-layout-item>
                                        </grid-layout>
                                    </form-section>
                                </div>

                                <div class="px-4 py-6 border-b sm:px-6">
                                    <form-section>
                                        <template slot="title">
                                            <router-link
                                                :to="{ name: 'applications.applicants.details.edit' }"
                                                class="no-underline text-blue"
                                            >
                                                Applicants
                                            </router-link>
                                        </template>
                                        <template slot="description">
                                            Check that <strong>your details</strong> are
                                            <strong>legally correct</strong> (i.e. match your ID).
                                        </template>

                                        <h6 class="mb-3">Your details</h6>

                                        <grid-layout>
                                            <grid-layout-item class="mb-3 sm:w-1/2">
                                                <div class="text-xs text-label">Name</div>
                                                <div class="mb-3 text-sm text-grey-darkest">
                                                    {{ applicant.first_name }} {{ applicant.last_name }}
                                                </div>

                                                <div class="text-xs text-label">Date of Birth</div>
                                                <div class="mb-3 text-sm text-grey-darkest">
                                                    {{ applicant.date_of_birth | moment('DD/MM/YYYY') }}
                                                </div>

                                                <div class="text-xs text-label">Telephone</div>

                                                <a-tel
                                                    v-if="applicant.telephone"
                                                    class="text-sm text-blue"
                                                    :telephone="applicant.telephone"
                                                />

                                                <base-na v-else />
                                            </grid-layout-item>

                                            <grid-layout-item class="sm:w-1/2">
                                                <div class="text-xs text-label">Address</div>
                                                <div class="text-sm text-grey-darkest">
                                                    <base-address :address="applicant.address" />
                                                </div>
                                            </grid-layout-item>
                                        </grid-layout>

                                        <template v-if="!applicant.is_co_applicant">
                                            <h6 class="mt-8 mb-3 md:mt-3">Co-Applicants</h6>

                                            <template v-if="coapplicants.length">
                                                <div
                                                    v-for="(coapplicant, index) in coapplicants"
                                                    :key="coapplicant.uuid"
                                                    class="relative mt-3 mb-6 border rounded last-child:mb-0"
                                                >
                                                    <header class="flex items-center px-4 py-3 text-xs border-b text-label bg-grey-lightest">
                                                        {{ index + 1 | ordinal }} Co-Applicant
                                                    </header>

                                                    <div class="px-4 pt-3">
                                                        <grid-layout>
                                                            <grid-layout-item class="sm:w-1/2">
                                                                <div class="text-xs text-label">Name</div>
                                                                <div class="mb-3 text-sm text-grey-darkest">
                                                                    {{ coapplicant.first_name }} {{ coapplicant.last_name }}
                                                                </div>
                                                            </grid-layout-item>

                                                            <grid-layout-item
                                                                v-if="coapplicant.email"
                                                                class="sm:w-1/2"
                                                            >
                                                                <div class="text-xs text-label">Email</div>
                                                                <a-mailto
                                                                    class="mb-3 text-sm text-blue"
                                                                    :email="coapplicant.email"
                                                                />
                                                            </grid-layout-item>

                                                            <grid-layout-item
                                                                v-if="coapplicant.telephone"
                                                                class="sm:w-1/2"
                                                            >
                                                                <div class="text-xs text-label">Telephone</div>
                                                                <a-tel
                                                                    class="mb-3 text-sm text-blue"
                                                                    :telephone="coapplicant.telephone"
                                                                />
                                                            </grid-layout-item>
                                                        </grid-layout>
                                                    </div>
                                                </div>
                                            </template>

                                            <template v-else>
                                                <p class="text-sm italic text-grey-darkest">You're applying alone.</p>
                                            </template>
                                        </template>
                                    </form-section>
                                </div>

                                <div class="px-4 py-6 border-b sm:px-6">
                                    <form-section>
                                        <template slot="title">
                                            <router-link
                                                :to="{ name: 'applications.applicants.references.edit' }"
                                                class="no-underline text-blue"
                                            >
                                                References
                                            </router-link>
                                        </template>
                                        <template slot="description">
                                            Check that your <strong>employer</strong> and <strong>previous landlord</strong> contact details are correct.
                                        </template>

                                        <h6 class="mb-3">Employment</h6>

                                        <template v-if="applicant.employers.data.length">
                                            <div
                                                v-for="(employer, index) in applicant.employers.data"
                                                :key="employer.uuid"
                                                class="relative mt-3 mb-6 border rounded last-child:mb-0"
                                            >
                                                <header class="flex items-center px-4 py-3 text-xs border-b text-label bg-grey-lightest">
                                                    {{ index + 1 | ordinal }} Employer
                                                </header>

                                                <div class="px-4 pt-3 border-b">
                                                    <grid-layout>
                                                        <grid-layout-item class="sm:w-1/2">
                                                            <div class="text-xs text-label">Employer's Name</div>
                                                            <div class="mb-3 text-sm text-grey-darkest">{{ employer.name }}</div>
                                                        </grid-layout-item>

                                                        <grid-layout-item class="sm:w-1/2">
                                                            <div class="text-xs text-label">Your Job Title/Position</div>
                                                            <div class="mb-3 text-sm text-grey-darkest">{{ employer.job_title }}</div>
                                                        </grid-layout-item>

                                                        <grid-layout-item>
                                                            <div class="text-xs text-label">Monthly take home</div>
                                                            <div class="mb-3 text-sm text-grey-darkest">{{ employer.salary | money(0) }}</div>
                                                        </grid-layout-item>

                                                        <grid-layout-item class="sm:w-1/2">
                                                            <div class="text-xs text-label">Started at</div>
                                                            <div class="mb-3 text-sm text-grey-darkest">{{ employer.started_at | moment('DD/MM/YYYY') }}</div>
                                                        </grid-layout-item>

                                                        <grid-layout-item class="sm:w-1/2">
                                                            <div class="text-xs text-label">Finished at</div>
                                                            <div class="mb-3 text-sm text-grey-darkest">
                                                                <template v-if="employer.finished_at">
                                                                    {{ employer.finished_at | moment('DD/MM/YYYY') }}
                                                                </template>
                                                                <base-na v-else />
                                                            </div>
                                                        </grid-layout-item>
                                                    </grid-layout>

                                                    <h6 class="mt-6 mb-3">Contact</h6>

                                                    <grid-layout>
                                                        <grid-layout-item class="sm:w-1/2">
                                                            <div class="text-xs text-label">Name</div>
                                                            <div class="mb-3 text-sm text-grey-darkest">
                                                                {{ employer.first_name }} {{ employer.last_name }}
                                                            </div>
                                                        </grid-layout-item>

                                                        <grid-layout-item
                                                            v-if="employer.email"
                                                            class="sm:w-1/2"
                                                        >
                                                            <div class="text-xs text-label">Email</div>
                                                            <a-mailto
                                                                class="mb-3 text-sm text-blue"
                                                                :email="employer.email"
                                                            />
                                                        </grid-layout-item>

                                                        <grid-layout-item
                                                            v-if="employer.telephone"
                                                            class="sm:w-1/2"
                                                        >
                                                            <div class="text-xs text-label">Telephone</div>
                                                            <a-tel
                                                                class="mb-3 text-sm text-blue"
                                                                :email="employer.telephone"
                                                            />
                                                        </grid-layout-item>
                                                    </grid-layout>
                                                </div>
                                            </div>
                                        </template>

                                        <template v-else>
                                            <p class="text-sm italic text-grey-darkest">No employment history provided.</p>
                                        </template>

                                        <h6 class="mt-8 mb-3 md:mt-3">Landlord</h6>

                                        <template v-if="applicant.landlord.first_name">
                                            <grid-layout>
                                                <grid-layout-item class="sm:w-1/2">
                                                    <div class="text-xs text-label">Name</div>
                                                    <div class="mb-3 text-sm text-grey-darkest">
                                                        {{ applicant.landlord.first_name }} {{ applicant.landlord.last_name }}
                                                    </div>
                                                </grid-layout-item>

                                                <grid-layout-item
                                                    v-if="applicant.landlord.email"
                                                    class="sm:w-1/2"
                                                >
                                                    <div class="text-xs text-label">Email</div>
                                                    <a-mailto
                                                        class="mb-3 text-sm text-blue"
                                                        :email="applicant.landlord.email"
                                                    />
                                                </grid-layout-item>

                                                <grid-layout-item
                                                    v-if="applicant.landlord.telephone"
                                                    class="sm:w-1/2"
                                                >
                                                    <div class="text-xs text-label">Telephone</div>
                                                    <a-tel
                                                        class="mb-3 text-sm text-blue"
                                                        :email="applicant.landlord.telephone"
                                                    />
                                                </grid-layout-item>
                                            </grid-layout>
                                        </template>

                                        <template v-else>
                                            <p class="text-sm italic text-grey-darkest">No previous landlord details provided.</p>
                                        </template>
                                    </form-section>
                                </div>

                                <div class="px-4 py-6 border-b sm:px-6">
                                    <form-section>
                                        <template slot="title">
                                            <span class="text-grey-darkest">Rentora</span>
                                        </template>
                                        <template slot="description">
                                            Which Rentora services do you want to use?
                                        </template>

                                        <label class="flex mb-3 cursor-pointer items-top">
                                            <input-checkbox
                                                v-model="form.wants_to"
                                                value="pay_by_card"
                                                class="flex-none mr-3"
                                            />

                                            <div class="text-grey-darkest">
                                                I want to pay my rent by credit/debit card.
                                            </div>
                                        </label>

                                        <label class="flex mb-3 cursor-pointer items-top">
                                            <input-checkbox
                                                v-model="form.wants_to"
                                                value="improve_credit_rating"
                                                class="flex-none mr-3"
                                            />

                                            <div class="text-grey-darkest">
                                                I want to improve my credit rating.
                                            </div>
                                        </label>

                                        <label class="flex cursor-pointer items-top">
                                            <input-checkbox
                                                v-model="form.wants_to"
                                                value="pay_rent_early"
                                                class="flex-none mr-3"
                                            />

                                            <div class="text-grey-darkest">
                                                I want to pay my rent early.
                                            </div>
                                        </label>
                                    </form-section>
                                </div>

                                <div class="px-4 py-6 border-b sm:px-6">
                                    <label class="flex cursor-pointer items-top md:pl-1/3">
                                        <input-checkbox
                                            v-model="has_confirmed"
                                            class="flex-none mr-3"
                                        />

                                        <div class="text-sm text-grey-darkest">
                                            I can confirm that this information is correct, and I grant permission for it to be shared with the listing's landlord.
                                            Additionally, I grant permission to current and former employers, landlords and references listed on this application
                                            to release my information for verification purposes.
                                        </div>
                                    </label>
                                </div>

                                <base-form-footer
                                    :show-required="false"
                                    class="rounded-b"
                                >
                                    <base-button
                                        :disabled="!has_confirmed"
                                        :is-loading="is_saving"
                                        type="submit"
                                        class="w-full btn-blue md:w-auto"
                                    >
                                        Submit Application
                                    </base-button>
                                </base-form-footer>

                                <error-modal
                                    v-if="store_error && $error.is(store_error, { exclude: [400, 422] })"
                                    :error="store_error"
                                />

                                <modal-success :is-open="show_created_modal">
                                    <template slot="heading">Application submitted!</template>

                                    <p>
                                        Your application has been submitted!
                                    </p>

                                    <template v-slot:footer>
                                        <div class="flex flex-col md:flex-row-reverse">
                                            <laroute-link
                                                :to="{
                                                    name: 'listings.index'
                                                }"
                                                class="text-center btn btn-green"
                                            >
                                                Return to Listings
                                            </laroute-link>

                                            <laroute-link
                                                :to="{
                                                    name: 'dashboard.index'
                                                }"
                                                class="mt-3 text-center btn md:mt-0 md:mr-3"
                                            >
                                                Go to Dashboard
                                            </laroute-link>
                                        </div>
                                    </template>
                                </modal-success>
                            </base-form>
                        </applicants-submission-store>
                    </template>
                </div>
            </promise-all>
        </applications-applicants-get>
    </applicants-find>
</template>

<script>
    import _ from 'lodash'
    import HeadMixin from '../../../../mixins/head'
    import PreloadedMixin from '../../../../mixins/preloaded'
    import ApplicantsFind from '../../../../components/applicants-find'
    import ApplicantsSubmissionStore from '../../../../components/applicants-submission-store'
    import ApplicationsApplicantsGet from '../../../../components/applications-applicants-get'
    import PromiseAll from '../../../../components/promise-all'

    export default {
        mixins: [
            HeadMixin,
            PreloadedMixin
        ],
        components: {
            ApplicantsFind,
            ApplicantsSubmissionStore,
            ApplicationsApplicantsGet,
            PromiseAll
        },
        data () {
            return {
                show_created_modal: false,
                has_confirmed: false,
                form: {
                    wants_to: []
                }
            }
        },
        methods: {
            head () {
                const address = _.get(this.preloaded('application'), 'data.listing.property.address.name')

                return {
                    title: [
                        'Confirm',
                        `${address} Application`
                    ]
                }
            },
            onSuccess () {
                setTimeout(() => {
                    this.show_created_modal = true
                }, 0)
            }
        }
    }
</script>
